/**
 * Color transformation utilities for dark mode support
 * Provides intelligent color adaptation for canvas content
 */

export type ColorMode = 'light' | 'dark';

/**
 * Color transformation strategies for different content types
 */
export interface ColorTransformOptions {
  /** Preserve color identity while adapting contrast */
  preserveHue?: boolean;
  /** Minimum contrast ratio to maintain */
  minContrast?: number;
  /** Whether this is a background or foreground color */
  type?: 'background' | 'foreground' | 'accent';
}

/**
 * Converts hex color to HSL
 */
function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
}

/**
 * Converts HSL to hex color
 */
function hslToHex(h: number, s: number, l: number): string {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Smart color transformation for dark mode
 * Preserves color identity while ensuring good contrast
 */
export function transformColorForDarkMode(
  lightColor: string, 
  options: ColorTransformOptions = {}
): string {
  const { preserveHue = true, type = 'background' } = options;
  
  // Handle transparent/rgba colors
  if (lightColor.includes('rgba') || lightColor.includes('rgb')) {
    return lightColor; // Keep as-is for now, could be enhanced
  }
  
  // Ensure hex format
  if (!lightColor.startsWith('#')) {
    return lightColor;
  }
  
  const [h, s, l] = hexToHsl(lightColor);
  
  // Transform based on color type and current lightness
  let newL = l;
  let newS = s;
  
  if (type === 'background') {
    // For backgrounds: light colors become darker, dark colors become lighter
    if (l > 50) {
      // Light background -> dark background
      newL = Math.max(15, 100 - l * 0.8);
    } else {
      // Dark background -> lighter dark background
      newL = Math.min(25, l + 10);
    }
    
    // Reduce saturation slightly for backgrounds
    newS = Math.max(s * 0.7, 10);
  } else if (type === 'foreground') {
    // For text/strokes: ensure high contrast
    if (l < 50) {
      // Dark text -> light text
      newL = Math.min(90, 100 - l * 0.5);
    } else {
      // Light text -> keep light or make lighter
      newL = Math.max(80, l);
    }
  } else if (type === 'accent') {
    // For accent colors: maintain vibrancy but adjust lightness
    if (l > 60) {
      newL = l * 0.7; // Darken bright accents
    } else if (l < 30) {
      newL = l + 20; // Lighten very dark accents
    }
    // Keep saturation high for accents
    newS = Math.min(s * 1.1, 100);
  }
  
  if (!preserveHue) {
    // Could shift hue for more dramatic dark mode transformation
    // For now, preserve hue
  }
  
  return hslToHex(h, newS, newL);
}

const DARK_MODE_WHITE_AS_FOREGROUND_GRAY = '#94a3b8';

function expandHexForWhiteCheck(hex: string): string | null {
  const c = hex.trim().toLowerCase();
  if (!c.startsWith('#')) return null;
  if (/^#[0-9a-f]{6}$/.test(c)) return c;
  if (/^#[0-9a-f]{3}$/.test(c)) {
    return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`;
  }
  return null;
}

function isWhiteColorForDarkForeground(color: string): boolean {
  const name = color.trim().toLowerCase();
  if (name === 'white') return true;
  return expandHexForWhiteCheck(color) === '#ffffff';
}

/**
 * Predefined color mappings for common UI colors
 */
export const COLOR_MAPPINGS: Record<string, { light: string; dark: string }> = {
  // Default shape colors
  '#f5f5f5': { light: '#f5f5f5', dark: '#2a2a2a' }, // Default fill
  '#333': { light: '#333333', dark: '#cccccc' }, // Default stroke
  '#333333': { light: '#333333', dark: '#cccccc' },
  
  // Selection colors
  '#2196f3': { light: '#2196f3', dark: '#64b5f6' },
  '#3478DD': { light: '#3478DD', dark: '#9DB5FF' },
  '#2E6BFF': { light: '#2E6BFF', dark: '#9DB5FF' },
  
  // Location-based colors (from PersonNode)
  '#CD853F': { light: '#CD853F', dark: '#deb887' }, // Italy
  '#4682B4': { light: '#4682B4', dark: '#87ceeb' }, // UK
  '#90EE90': { light: '#90EE90', dark: '#98fb98' }, // Vietnam
  
  // Connector colors
  '#334155': { light: '#334155', dark: '#94a3b8' }, // Default connector
  
  // Preset colors from tools
  '#000000': { light: '#000000', dark: '#ffffff' },
  '#ffffff': { light: '#ffffff', dark: '#1a1a1a' },
  '#ef4444': { light: '#ef4444', dark: '#f87171' },
  '#f97316': { light: '#f97316', dark: '#fb923c' },
  '#eab308': { light: '#eab308', dark: '#fbbf24' },
  '#22c55e': { light: '#22c55e', dark: '#4ade80' },
  '#3b82f6': { light: '#3b82f6', dark: '#60a5fa' },
  '#8b5cf6': { light: '#8b5cf6', dark: '#a78bfa' },
  '#ec4899': { light: '#ec4899', dark: '#f472b6' },
  '#6b7280': { light: '#6b7280', dark: '#9ca3af' },
};

/**
 * Get the appropriate color for the current theme mode
 */
export function getThemeAwareColor(
  color: string, 
  mode: ColorMode,
  options: ColorTransformOptions = {}
): string {
  if (mode === 'dark' && options.type === 'foreground' && isWhiteColorForDarkForeground(color)) {
    return DARK_MODE_WHITE_AS_FOREGROUND_GRAY;
  }
  const mapping = COLOR_MAPPINGS[color.toLowerCase()];
  if (mapping) {
    return mapping[mode];
  }
  
  // If no mapping exists and we're in dark mode, transform the color
  if (mode === 'dark') {
    return transformColorForDarkMode(color, options);
  }
  
  // Return original color for light mode
  return color;
}

/**
 * Hook to get theme-aware colors
 */
export function useThemeAwareColor() {
  // This will be implemented to use the theme context
  return (color: string, options?: ColorTransformOptions) => {
    // For now, detect dark mode from document class
    const isDark = document.documentElement.classList.contains('dark');
    return getThemeAwareColor(color, isDark ? 'dark' : 'light', options);
  };
}

/**
 * Generate dark mode variants for color palettes
 */
export function generateDarkModeColorPalette(lightColors: string[][]): string[][] {
  return lightColors.map(row => 
    row.map(color => getThemeAwareColor(color, 'dark', { type: 'accent' }))
  );
}
